import React, { useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Button, Checkbox, Form, Select, Spin, Table, Typography, Switch, Space } from 'antd'
import Highlighter from 'react-highlight-words'
import uniq from 'lodash/uniq'
import flatten from 'lodash/flatten'

import ProjectSelector from '../../ProjectSelector'
import { getEstimateFieldByProject } from '../../../services/estimate-field'
import { getReportByProject } from '../../../services/application'
import useCategory from '../../hooks/useCategory'
import { APP_STATUS_AGREE, APP_STATUS_DELIVERY, APP_STATUS_DONE, APP_STATUS_PAY, statuses } from '../constants'
import {
  calcImplSumAmount,
  calcSumAmount,
  correctedPriceNoVat,
  priceNoVat,
  priceSumNoVat,
  sumNoVatByUnit,
  sumNoVatByUnitWithCorrect,
} from '../../../utils/calculation'
import useTableFilter from '../../hooks/useTableFilter'
import exportToXLS from './exportToXLS'
import { userSelector } from '../../../store/session'
import { getUserProjectNameById } from '../report-utils'
import { getActs } from '../../../services/acts'
import { formatMoney, formatSum } from '../../../utils/number-utils'

const { Text } = Typography

const get = require('lodash/get')

const DEFAULT_HEIGHT = window.innerHeight || 600

const defaultStatuses = [APP_STATUS_AGREE, APP_STATUS_PAY, APP_STATUS_DELIVERY, APP_STATUS_DONE]

const baseCellStyling = {
  padding: 8,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
}

const numberCellStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  overflowWrap: 'normal',
}

const getCellStyling = (index, arr) => ({
  borderBottom: arr.length - 1 > index ? '1px solid #f0f0f0' : 0,
  ...baseCellStyling,
})

const transformReportsResult = (reports, isImplemented) => {
  const results = reports[1]
  const acts = reports[2]
  const actItems =
    acts?.reduce((acc, act) => {
      if (act?.act_items) {
        acc.push(...act?.act_items)
      }

      return acc
    }, []) || []
  const flatten = reports[0]?.reduce((acc, estimate) => {
    const implementations = results.reduce((implementationAcc, application) => {
      const implementationArr = application?.implementation?.implementation_items.filter(
        (implementation) => implementation?.application_item?.estimate_field?.id === estimate.id
      )
      if (implementationArr && implementationArr.length > 0) {
        implementationArr.forEach((implementation) => {
          implementationAcc.push({
            applicationId: application.id,
            ...implementation,
          })
        })
      }
      return implementationAcc
    }, [])

    if (!isImplemented || (isImplemented && implementations.length > 0)) {
      acc[estimate.id] = {}
      acc[estimate.id].estimate = estimate
      acc[estimate.id].implementations = implementations
      acc[estimate.id].acts = actItems.filter((act) => act?.estimate?.id === estimate.id)
    }

    return acc
  }, {})
  return flatten ? Object.values(flatten) : []
}

const filterByEstimateName = (value) => (record) => record.name?.toString().toLowerCase().includes(value.toLowerCase())

const filterData = (data, filter) => {
  let { 'estimate.name': estimateName } = filter
  return estimateName ? [data[0].filter(filterByEstimateName(estimateName)), data[1], data[2]] : data
}

const EstimateReport = () => {
  const [form] = Form.useForm()
  const project = Form.useWatch('project', form)
  const [categories] = useCategory(project)
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentData, setCurrentData] = useState([])
  const [isImplemented, setIsImplemented] = useState(true)
  const [isShortVersion, setIsShortVersion] = useState(false)
  const [xScrollEnabled, setXScrollEnabled] = useState(false)

  const [filter, searchText, searchedColumn, getColumnSearchProps] = useTableFilter()

  const estimateStyling = { backgroundColor: !isShortVersion ? '#feff00' : 'white', ...baseCellStyling }
  const implementationStyling = { backgroundColor: !isShortVersion ? '#a5c1e0' : 'white', ...baseCellStyling }
  const actsStyling = { backgroundColor: !isShortVersion ? '#cce1a9' : 'white', ...baseCellStyling }

  const { user } = useSelector(userSelector)

  const columns = [
    {
      title: 'Позиция',
      key: 'name',
      dataIndex: 'estimate',
      width: 200,
      ...getColumnSearchProps('estimate.name'),
      onFilter: (value, record) => get(record, 'estimate.name').toString().toLowerCase().includes(value.toLowerCase()),
      render: (_, record) =>
        searchedColumn === 'estimate.name' ? (
          <Highlighter
            highlightStyle={{
              backgroundColor: '#ffc069',
              padding: 0,
            }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={get(record, 'estimate.name') ? get(record, 'estimate.name').toString() : ''}
          />
        ) : (
          get(record, 'estimate.name')
        ),
    },
    {
      title: 'Категория',
      key: 'category',
      dataIndex: 'estimate',
      width: 150,
      render: (estimate) => estimate?.category,
    },
    {
      title: 'Ед. изм.',
      key: 'unit',
      dataIndex: 'estimate',
      width: 80,
      render: (estimate) => estimate?.unit,
    },
    {
      title: 'Смета',
      children: [
        {
          title: 'Количество',
          key: 'amount',
          dataIndex: 'estimate',
          width: 100,
          className: 'no-padding-cell-report',
          render: (estimate) => (
            <div className="full-height-div">
              <div style={{ ...estimateStyling, ...numberCellStyle }}>
                <strong>{formatSum(estimate?.amount)}</strong>
              </div>
            </div>
          ),
        },
        {
          title: 'Цена за ед, без НДС',
          key: 'price',
          dataIndex: 'estimate',
          width: 150,
          className: 'no-padding-cell-report',
          render: (estimate) => (
            <div className="full-height-div">
              <div style={estimateStyling}>{formatMoney(estimate?.price)}</div>
            </div>
          ),
        },
        {
          title: 'Сумма, без НДС',
          key: 'sum',
          dataIndex: 'estimate',
          width: 150,
          className: 'no-padding-cell-report',
          render: (estimate) => (
            <div className="full-height-div">
              <div style={estimateStyling}>{formatMoney(estimate?.price * estimate?.amount)}</div>
            </div>
          ),
        },
      ],
    },
    {
      title: 'Факт',
      children: [
        {
          title: () => <div style={{ padding: 8 }}>Номер заявки</div>,
          key: 'application_id',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 70,
          hidden: true,
          render: (implementations) => (
            <div className="full-height-div">
              {implementations &&
                implementations.map((item, index, arr) =>
                  item?.applicationId ? (
                    <div style={{ ...implementationStyling, ...getCellStyling(index, arr) }}>
                      <a href={`/applications?state=all&id=${item?.applicationId}`} target="_blank" rel="noreferrer">
                        {item?.applicationId}
                      </a>
                    </div>
                  ) : null
                )}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Кол-во</div>,
          key: 'implementation_count',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 100,
          hidden: true,
          render: (implementations, record) => {
            const implementationAmount = implementations.reduce((acc, item) => {
              const finalAmount = calcSumAmount(item)
              return acc + finalAmount
            }, 0)
            const isOver = implementationAmount > record?.estimate?.amount
            return (
              <div className="full-height-div">
                {implementations &&
                  implementations.map((item, index, arr) => (
                    <div
                      style={{
                        backgroundColor: isOver ? '#eca8b2' : 'white',
                        ...getCellStyling(index, arr),
                        ...numberCellStyle,
                      }}
                    >
                      {formatSum(calcSumAmount(item))}
                    </div>
                  ))}
              </div>
            )
          },
        },
        {
          title: () => <div style={{ padding: 8 }}>Кор-ка кол-ва</div>,
          key: 'implementation_corrected_count',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 100,
          hidden: true,
          render: (implementations, record) => {
            return (
              <div className="full-height-div">
                {implementations &&
                  implementations.map((item, index, arr) => (
                    <div style={{ backgroundColor: 'white', ...getCellStyling(index, arr), ...numberCellStyle }}>
                      {formatSum(item?.correctedAmount)}
                    </div>
                  ))}
              </div>
            )
          },
        },
        {
          title: () => <div style={{ padding: 8 }}>Ед.изм.</div>,
          key: 'implementation_unit',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 100,
          hidden: true,
          render: (implementations, record) => (
            <div className="full-height-div">
              {implementations.map((item, index, arr) => (
                <div style={getCellStyling(index, arr)}>
                  <Text type={item?.unit !== record?.estimate?.unit ? 'danger' : ''}>{item.unit}</Text>
                </div>
              ))}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Цена за ед. без НДС</div>,
          key: 'implementation_price',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 150,
          hidden: true,
          render: (implementations) => (
            <div className="full-height-div">
              {implementations.map((item, index, arr) => (
                <div style={getCellStyling(index, arr)}>{formatMoney(priceNoVat(item))}</div>
              ))}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Кор-ка стоимости</div>,
          key: 'implementation_corrected_price',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 150,
          hidden: true,
          render: (implementations) => (
            <div className="full-height-div">
              {implementations.map((item, index, arr) => (
                <div style={getCellStyling(index, arr)}>{formatMoney(correctedPriceNoVat(item))}</div>
              ))}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Сумма по позиции без НДС</div>,
          key: 'implementation_sum',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 150,
          hidden: true,
          render: (implementations) => (
            <div className="full-height-div">
              {implementations.map((item, index, arr) => (
                <div style={{ ...implementationStyling, ...getCellStyling(index, arr) }}>
                  {formatMoney(priceSumNoVat(item))}
                </div>
              ))}
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Всего кол-во</div>,
          key: 'implementation_amount_sum',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 150,
          render: (implementations) => (
            <div className="full-height-div">
              {
                <div style={{ ...implementationStyling, ...numberCellStyle }}>
                  <strong>{formatSum(calcImplSumAmount(implementations))}</strong>
                </div>
              }
            </div>
          ),
        },
        {
          title: () => <div style={{ padding: 8 }}>Сумма, руб. без НДС</div>,
          key: 'implementation_sum_unit',
          dataIndex: 'implementations',
          className: 'no-padding-cell-report',
          width: 150,
          render: (implementations, record) => {
            const sumObject = sumNoVatByUnitWithCorrect(implementations)
            const estimateSum = record?.estimate?.price * record?.estimate?.amount
            const implementationSum = Object.values(sumObject).reduce((acc, item) => {
              return acc + item.sum
            }, 0)
            const isOver = implementationSum > estimateSum
            const sums = Object.values(sumObject)
            const defaultColor = isShortVersion ? 'white' : '#a5c1e0'
            return (
              <div className="full-height-div">
                {sums.length > 0 ? (
                  sums.map((item, index, arr) =>
                    item.sum ? (
                      <div
                        style={{ backgroundColor: isOver ? '#eca8b2' : defaultColor, ...getCellStyling(index, arr) }}
                      >
                        {formatMoney(item.sum)}
                      </div>
                    ) : (
                      <div style={implementationStyling}>0</div>
                    )
                  )
                ) : (
                  <div style={implementationStyling}>0</div>
                )}
              </div>
            )
          },
        },
      ].filter((item) => (isShortVersion ? true : !item.hidden)),
    },
    {
      title: 'Выполнено по актам',
      children: [
        {
          title: () => <div style={{ padding: 8 }}>Количество</div>,
          key: 'act_amount_sum',
          dataIndex: 'acts',
          width: 150,
          className: 'no-padding-cell-report',
          render: (acts) => {
            const sum = acts.reduce((acc, act) => acc + act.amount, 0)
            return (
              <div className="full-height-div">
                <div style={{ ...actsStyling, ...numberCellStyle }}>
                  <strong>{formatSum(sum)}</strong>
                </div>
              </div>
            )
          },
        },
        {
          title: () => <div style={{ padding: 8 }}>Цена за ед, без НДС</div>,
          key: 'act_price',
          dataIndex: 'acts',
          width: 150,
          className: 'no-padding-cell-report',
          render: (acts) => {
            const uniqueActPrices = uniq(acts.map((item) => priceNoVat(item)))
            return (
              <div className="full-height-div">
                {uniqueActPrices?.length > 0 ? (
                  uniqueActPrices.map((price, index, arr) => (
                    <div style={{ ...actsStyling, ...getCellStyling(index, arr) }}>{formatMoney(price)}</div>
                  ))
                ) : (
                  <div style={{ ...actsStyling }}>{0}</div>
                )}
              </div>
            )
          },
        },
        {
          title: () => <div style={{ padding: 8 }}>Сумма, без НДС</div>,
          key: 'act_price_sum',
          dataIndex: 'acts',
          width: 150,
          className: 'no-padding-cell-report',
          render: (acts) => {
            const actsSum = acts.reduce((acc, act) => acc + priceSumNoVat(act), 0)
            return (
              <div className="full-height-div">
                <div style={{ ...actsStyling }}>{formatMoney(actsSum)}</div>
              </div>
            )
          },
        },
      ],
    },
  ]

  const onFormSubmit = async ({ project, statuses, estimateCategories = [], isImplemented }) => {
    setIsLoading(true)
    setIsImplemented(isImplemented)

    try {
      const pagination = { page: 1, pageSize: 9999 }
      let estimateReport = getEstimateFieldByProject(project, '', estimateCategories, pagination)
      let projectReport = getReportByProject(project, { statuses, implDetalisation: false })
      let actReport = getActs({ project }, pagination)

      const estimateResult = await estimateReport
      const projectResult = await projectReport
      const actResult = await actReport

      const estimate = estimateResult?.data?.data?.attributes?.results
      const projects = projectResult?.data?.data?.attributes?.results
      const acts = actResult?.data?.results
      setData([estimate, projects, acts])
    } finally {
      setIsLoading(false)
    }
  }

  let ds = useMemo(() => {
    const transformed = transformReportsResult(filterData(data, filter), isImplemented)
    setCurrentData(transformed)
    return transformed
  }, [data, filter, isImplemented])

  useEffect(() => {
    if (!isLoading && data && data.length > 0) {
      setXScrollEnabled(true)
    }
  }, [isLoading, data])

  return (
    <Spin spinning={isLoading}>
      <div style={{ paddingTop: 10 }}>
        <Form
          initialValues={{ statuses: defaultStatuses }}
          form={form}
          layout="vertical"
          style={{ display: 'flex' }}
          onFinish={onFormSubmit}
        >
          <Form.Item
            style={{ width: 250, maxWidth: 250 }}
            rules={[{ required: true, message: 'Выберите проект' }]}
            label="Проект"
            name="project"
          >
            <ProjectSelector />
          </Form.Item>
          <Form.Item style={{ minWidth: 200, marginLeft: 15 }} label="Категории" name="estimateCategories">
            <Select
              mode="multiple"
              allowClear
              options={categories.map((category) => ({
                value: category,
                label: category,
              }))}
              dropdownMatchSelectWidth={false}
            />
          </Form.Item>
          <Form.Item style={{ marginLeft: 15, minWidth: 100 }} label="Статусы заявки" name="statuses">
            <Select mode="multiple" allowClear options={statuses} dropdownMatchSelectWidth={false} />
          </Form.Item>
          <Form.Item
            style={{ textAlign: 'center', marginLeft: 15, minWidth: 130 }}
            label="Только с заявками"
            name="isImplemented"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox />
          </Form.Item>
          <Form.Item label="" style={{ marginLeft: 15, marginTop: 30 }}>
            <Button onClick={() => form.submit()}>Сформировать</Button>
          </Form.Item>
          <Form.Item label="" style={{ marginLeft: 15, marginTop: 30 }}>
            <Button
              disabled={data?.length < 1}
              onClick={() => exportToXLS(currentData, getUserProjectNameById(user, project))}
            >
              Скачать
            </Button>
            <Button
              style={{ marginLeft: 15 }}
              disabled={data?.length < 1}
              onClick={() => exportToXLS(currentData, getUserProjectNameById(user, project), false)}
            >
              Скачать полный отчет
            </Button>
          </Form.Item>
        </Form>
        <Space
          align="center"
          style={{
            marginBottom: 16,
          }}
        >
          Расширенная версия: <Switch checked={isShortVersion} onChange={setIsShortVersion} />
        </Space>
        <Table
          columns={columns}
          dataSource={ds}
          bordered
          size="small"
          pagination={false}
          scroll={{ x: xScrollEnabled ? 2500 : undefined, y: DEFAULT_HEIGHT - 240 }}
          onChange={(pagination, filters, sorter, extra) => {
            setCurrentData(extra.currentDataSource)
          }}
          summary={(pageData) => {
            const units = uniq(pageData.map(({ estimate }) => estimate.unit))
            const amount = pageData.reduce((acc, { estimate }) => {
              if (estimate.amount > 0) {
                return acc + estimate.amount
              }
              return acc
            }, 0)

            const estimateSum = pageData.reduce((acc, { estimate }) => {
              if (estimate && estimate?.price && estimate?.amount) {
                const sum = estimate?.price * estimate?.amount
                return acc + sum
              }

              return acc
            }, 0)

            const implementations = flatten(pageData.map(({ implementations }) => implementations))

            const implementationSum = pageData.reduce((acc, { implementations }) => {
              const sumObject = sumNoVatByUnit(implementations)
              const implementationEstimateSum = Object.values(sumObject).reduce((acc, item) => {
                return acc + item.sum
              }, 0)
              return acc + implementationEstimateSum
            }, 0)

            const implCorrectedPriceSum = pageData.reduce(
              (acc, { implementations }) =>
                acc + implementations.reduce((implAcc, item) => implAcc + correctedPriceNoVat(item), 0),
              0
            )

            return (
              <Table.Summary>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Итого:</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} colSpan={2}></Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    <div style={numberCellStyle}>
                      <strong>{units.length === 1 ? formatSum(amount) : ''}</strong>
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}></Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>
                    <Text strong>{formatMoney(estimateSum)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6}></Table.Summary.Cell>
                  {isShortVersion && (
                    <Table.Summary.Cell index={7}>
                      <div style={numberCellStyle}>
                        <strong>{formatSum(calcImplSumAmount(implementations))}</strong>
                      </div>
                    </Table.Summary.Cell>
                  )}
                  {isShortVersion && <Table.Summary.Cell index={8} colSpan={3}></Table.Summary.Cell>}
                  {isShortVersion && (
                    <Table.Summary.Cell index={11}>
                      <Text strong>{formatMoney(implCorrectedPriceSum)}</Text>
                    </Table.Summary.Cell>
                  )}
                  <Table.Summary.Cell index={12}>
                    <Text strong>{formatMoney(implementationSum)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )
          }}
        />
      </div>
    </Spin>
  )
}

export default EstimateReport
